import axios from 'axios';
import { checkAndFixToken } from '../utils/browserTokenCheck';
import { getValidToken, isDemoToken } from '../utils/tokenUtils';

// База API:
// - В production ВСЕГДА используем относительные пути (тот же домен), потому что nginx проксирует /api/
//   и CSP на домене обычно "connect-src 'self'".
// - В development можно задать REACT_APP_API_URL для обращения к другому хосту.
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? ''
    : (process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : '');

// Логируем конфигурацию для отладки (только в development)
if (process.env.NODE_ENV === 'development') {
  // Отладочная информация удалена для production
  }

/** Импорт Excel резервной копии прокси + синхронизация в Kraken (сотни прокси — до ~1 ч). */
const PROXY_BACKUP_IMPORT_TIMEOUT_MS = 3600000;

/** Полный бэкап: создание, загрузка архива, pg_restore (до ~1 ч). */
const FULL_BACKUP_TIMEOUT_MS = PROXY_BACKUP_IMPORT_TIMEOUT_MS;

/** Один in-flight запрос на тяжёлый список портов (Strict Mode / двойные эффекты не штормят API). */
let getAllKrakenModemsInFlight = null;

/** Фоновая sync-with-kraken: polling статуса (nginx default 60s → 504 при блокирующем HTTP). */
const SYNC_WITH_KRAKEN_POLL_INTERVAL_MS = 2000;
const SYNC_WITH_KRAKEN_POLL_TIMEOUT_MS = 600000;

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const waitForSyncProxiesWithKrakenTask = async (taskId, { onProgress } = {}) => {
  const deadline = Date.now() + SYNC_WITH_KRAKEN_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    const statusResponse = await api.get(
      `/api/proxies/sync-with-kraken/status/${encodeURIComponent(taskId)}`
    );
    const task = statusResponse.data;

    if (typeof onProgress === 'function') {
      onProgress(task);
    }

    if (task.status === 'completed') {
      return {
        data: task.result,
        status: 200,
        statusText: 'OK',
        headers: statusResponse.headers,
        config: statusResponse.config,
      };
    }

    if (task.status === 'error') {
      const detail = task.error || 'Ошибка синхронизации прокси с Kraken';
      const err = new Error(typeof detail === 'string' ? detail : 'Ошибка синхронизации');
      err.response = { status: 500, data: { detail } };
      throw err;
    }

    await sleep(SYNC_WITH_KRAKEN_POLL_INTERVAL_MS);
  }

  const timeoutDetail = 'Таймаут ожидания синхронизации прокси с Kraken (10 минут)';
  const err = new Error(timeoutDetail);
  err.response = { status: 504, data: { detail: timeoutDetail } };
  throw err;
};

// Создание экземпляра axios
const api = axios.create({
  baseURL: API_BASE_URL,
  // Базовые запросы разделов UI должны завершаться заметно быстрее,
  // иначе страницы визуально "зависают" в вечной загрузке.
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'}});

// Создание экземпляра axios с увеличенным таймаутом для операций удаления
const deleteApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 минут для операций удаления больших объемов данных
  headers: {
    'Content-Type': 'application/json'}});

// Добавляем токен авторизации для deleteApi
deleteApi.interceptors.request.use(
  (config) => {
    const token = checkAndFixToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Экземпляр с увеличенным таймаутом для долгих операций (перезагрузка Kraken ~2 мин, reboot)
const longOperationApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 3 минуты для restart-kraken (kraken --restart ~2 мин) и reboot-server
  headers: { 'Content-Type': 'application/json' }
});
longOperationApi.interceptors.request.use(
  (config) => {
    const token = checkAndFixToken();
    if (isKrakenRequest(config.url || '') && !getValidToken()) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(new Error('Auth required'));
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const triggerBlobDownload = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const parseFetchErrorDetail = async (response) => {
  let detail = response.statusText || 'Ошибка запроса';
  try {
    const errBody = await response.text();
    try {
      const json = JSON.parse(errBody);
      detail = json.detail || errBody;
    } catch {
      detail = errBody || detail;
    }
  } catch {
    // оставляем statusText
  }
  return detail;
};

/** Распарсить detail из axios-ошибки (в т.ч. когда responseType: blob). */
export const parseApiErrorDetail = async (error, fallback = 'Неизвестная ошибка') => {
  const data = error?.response?.data;
  if (!data) {
    return error?.message || fallback;
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        return json.detail || text || fallback;
      } catch {
        return text || fallback;
      }
    } catch {
      return fallback;
    }
  }
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  if (data.detail && typeof data.detail === 'object') {
    return data.detail.message || data.detail.error || JSON.stringify(data.detail);
  }
  return error?.message || fallback;
};

/** Сообщение об ошибке для dry_run / restore полного бэкапа. */
export const formatFullBackupArchiveError = (error, fallback = 'Неизвестная ошибка') => {
  const status = error?.response?.status;
  if (status === 504) {
    return (
      'Превышено время ожидания nginx (504). Проверка или восстановление большого SSH-бэкапа '
      + 'может занимать несколько минут — увеличьте proxy_read_timeout для /api/backup/full.'
    );
  }
  if (status === 413) {
    return error.response?.data?.detail || 'Размер архива превышает лимит сервера';
  }
  return error.response?.data?.detail || error.message || fallback;
};

const getAuthTokenOrThrow = () => {
  const token = checkAndFixToken();
  if (!token) {
    throw new Error('Требуется авторизация');
  }
  return token;
};

const triggerIframeDownload = (url) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText =
    'position:fixed;left:-9999px;top:-9999px;width:0;height:0;border:0;opacity:0;pointer-events:none';
  iframe.src = url;
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), 180000);
};

const requestBackupDownloadTicket = async (downloadUrlPath, source = 'auto') => {
  const token = getAuthTokenOrThrow();
  const ticketUrlPath = downloadUrlPath.replace('/download/', '/download-ticket/');
  const sourceQuery = source && source !== 'auto'
    ? `?source=${encodeURIComponent(source)}`
    : '';
  const response = await fetch(`${API_BASE_URL}${ticketUrlPath}${sourceQuery}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(await parseFetchErrorDetail(response));
  }

  const payload = await response.json();
  if (!payload?.download_url) {
    throw new Error('Сервер не вернул ссылку для скачивания');
  }
  return `${API_BASE_URL}${payload.download_url}`;
};

/**
 * Скачать файл с авторизацией.
 * /download/ — ticket + скрытый iframe (без fetch/blob, стабильно для 100MB–6GB и SSH-туннелей).
 */
export const downloadAuthenticatedFile = async (urlPath, filename, source = 'auto') => {
  if (/\/download\//.test(urlPath)) {
    const downloadUrl = await requestBackupDownloadTicket(urlPath, source);
    triggerIframeDownload(downloadUrl);
    return;
  }

  const token = getAuthTokenOrThrow();
  const response = await fetch(`${API_BASE_URL}${urlPath}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(await parseFetchErrorDetail(response));
  }

  triggerBlobDownload(await response.blob(), filename || 'download');
};

/** POST-экспорт с телом JSON → скачивание файла (резервная копия прокси и др.). */
export const downloadAuthenticatedPostFile = async (urlPath, body, filename) => {
  const token = getAuthTokenOrThrow();
  const response = await fetch(`${API_BASE_URL}${urlPath}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(await parseFetchErrorDetail(response));
  }

  const blob = await response.blob();
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      throw new Error(json.detail || text || 'Ошибка экспорта');
    } catch (parseError) {
      if (parseError instanceof SyntaxError) {
        throw new Error(text || 'Ошибка экспорта');
      }
      throw parseError;
    }
  }

  triggerBlobDownload(blob, filename);
};

// Интерцептор для отладки запросов удален для production

const isApiRequest = (url = '') => url.includes('/api/');
const isKrakenRequest = (url = '') =>
  url.includes('/api/kraken') || url.includes('/api/kraken-connections');

// Интерцептор для добавления токена авторизации
api.interceptors.request.use(
  (config) => {
    // Проверяем и исправляем токен перед каждым запросом
    const token = checkAndFixToken();
    const validToken = getValidToken();
    if (isKrakenRequest(config.url || '') && !validToken) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(new Error('Auth required'));
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для отладки ответов удален для production

// Интерцептор для обработки ошибок и автоматического обновления токенов
api.interceptors.response.use(
  (response) => {
    // Логируем успешные ответы для отладки
    // Логируем структуру данных для proxy-checker API
    if (response.config.url && response.config.url.includes('proxy-checker')) {
      // Отладочная информация удалена для production
    }
    return response;
  },
  async (error) => {
    // Обработка ошибок API
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && isApiRequest(originalRequest?.url || '')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !(originalRequest.url && originalRequest.url.includes('/api/auth/refresh'))
    ) {
      originalRequest._retry = true;
      
      const token = getValidToken();
      
      if (!token) {
        // Токена нет или он недействителен – дальнейшие попытки бесполезны
        return Promise.reject(error);
      }

      // Если это демо токен, не пытаемся его обновлять
      if (isDemoToken(token)) {
        return Promise.reject(error);
      }
      
      try {
        // Пытаемся обновить токен
        const response = await apiService.refreshToken();
        const newToken = response.data.access_token;
        
        // Сохраняем новый токен
        localStorage.setItem('token', newToken);
        
        // Обновляем заголовок авторизации для повторного запроса
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Повторяем оригинальный запрос
        return api(originalRequest);
      } catch (refreshError) {
        // Если обновление токена не удалось, перенаправляем на страницу входа
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export const apiService = {
  // Аутентификация
  login: (username, password) => api.post('/api/auth/login', { username, password }),
  demoLogin: () => api.post('/api/auth/demo-login'),
  register: (userData) => api.post('/api/auth/register', userData),
  getCurrentUser: () => api.get('/api/auth/me'),
  refreshToken: () => api.post('/api/auth/refresh'),
  logout: () => api.post('/api/auth/logout'),
  getUsers: () => api.get('/api/auth/users'),

  // Настройки модемов по умолчанию
  getModemDefaults: () => api.get('/api/settings/modem-defaults'),
  setModemDefaults: (data) => api.post('/api/settings/modem-defaults', data),
  // Настройки личного кабинета клиента
  getCabinetDefaults: () => api.get('/api/settings/cabinet-defaults'),
  setCabinetDefaults: (data) => api.post('/api/settings/cabinet-defaults', data),
  checkTelegramBotProxiesHealth: (data) => api.post(
    '/api/settings/telegram-bot-proxies/health',
    data,
    { timeout: 90000 },
  ),
  getCabinetPofCatalog: () => api.get('/api/settings/cabinet-pof-catalog'),
  getModemSettingsResync: () => api.get('/api/settings/modem-settings-resync'),
  setModemSettingsResync: (data) => api.post('/api/settings/modem-settings-resync', data),
  postModemSettingsResyncRunNow: () => api.post('/api/settings/modem-settings-resync/run-now'),
  getKrakenProxyCleanup: () => api.get('/api/settings/kraken-proxy-cleanup'),
  setKrakenProxyCleanup: (data) => api.post('/api/settings/kraken-proxy-cleanup', data),
  postKrakenProxyCleanupRunNow: () => api.post('/api/settings/kraken-proxy-cleanup/run-now'),
  getOperatorTemplates: () => api.get('/api/settings/operator-templates'),
  saveOperatorTemplates: (data) => api.post('/api/settings/operator-templates', data),
  previewOperatorTemplates: (values) => api.post('/api/settings/operator-templates/preview', { values }),
  getIntegrationsSettings: () => api.get('/api/settings/integrations'),
  setIntegrationsSettings: (data) => api.post('/api/settings/integrations', data),
  
  // Клиенты
  getClients: (params = {}) => api.get('/api/clients', {
    params,
    paramsSerializer: { indexes: null },
  }),
  getClientFilterOptions: (params = {}) => api.get('/api/clients/filter-options', { params }),
  /** Полный клиент по id (актуальный cabinet_balance для drawer) */
  getClient: (clientId) => api.get(`/api/clients/${clientId}`),
  /** Staff: plaintext proxy-auth пароль (аудит на бэкенде) */
  revealClientProxyPassword: (clientId) => api.post(`/api/clients/${clientId}/reveal-password`),
  getClientActivityLog: (clientId, params = {}) =>
    api.get(`/api/clients/${clientId}/activity-log`, { params }),
  createClient: (clientData) => api.post('/api/clients', clientData),
  updateClient: (clientId, clientData) => api.put(`/api/clients/${clientId}`, clientData),
  deleteClient: (clientId) => api.delete(`/api/clients/${clientId}`),
  bulkDeleteClients: (clientIds) => deleteApi.post('/api/clients/bulk-delete', { client_ids: clientIds }),
  bulkDisableClients: (clientIds) => api.post('/api/clients/bulk-disable', { client_ids: clientIds }),
  bulkEnableClients: (clientIds) => api.post('/api/clients/bulk-enable', { client_ids: clientIds }),
  bulkUpdateClients: (clientIds, updateData = {}) => api.post('/api/clients/bulk-update', {
    client_ids: clientIds,
    ...updateData,
  }),
  toggleClientStatus: (clientId) => api.post(`/api/clients/${clientId}/toggle-status`),
  getClientCabinetProxyGroups: (clientId) => api.get(`/api/v1/admin/cabinet/clients/${clientId}/proxy-groups`),
  /** Список billing-заказов клиента (как во вкладках «Заказы/Операции» ЛК). */
  getAdminClientCabinetOrders: (clientId) => api.get(`/api/v1/admin/cabinet/clients/${clientId}/orders`),
  /** Отчёт по операциям баланса всех клиентов (Настройки → Логи по операциям). */
  getAdminCabinetBalanceOperations: (params = {}) =>
    api.get('/api/v1/admin/cabinet/balance-operations', { params }),
  /** Карточка заказа ЛК (как GET /api/v1/client/orders/:id у клиента). */
  getAdminClientCabinetOrder: (clientId, orderId) =>
    api.get(`/api/v1/admin/cabinet/clients/${clientId}/orders/${orderId}`),
  /** Полный возврат оплаты заказа ЛК через АТОЛ Pay. */
  postAdminClientCabinetOrderRefund: (clientId, orderId) =>
    api.post(`/api/v1/admin/cabinet/clients/${clientId}/orders/${orderId}/refund`),
  /** Отмена зависшего заказа покупки/продления с возвратом на баланс ЛК. */
  postAdminClientCabinetOrderCancelStuck: (clientId, orderId) =>
    api.post(`/api/v1/admin/cabinet/clients/${clientId}/orders/${orderId}/cancel-stuck`),
  /** Ручной возврат на баланс ЛК (сумма в теле запроса). */
  postAdminClientCabinetBalanceRefund: (clientId, body) =>
    api.post(`/api/v1/admin/cabinet/clients/${clientId}/balance-refund`, body),
  
  // Тарифные планы
  getTariffPlans: () => api.get('/api/tariff-plans'),
  
  // Гибкие тарифы
  getFlexibleTariffs: () => api.get('/api/flexible-tariffs'),
  createFlexibleTariff: (tariffData) => api.post('/api/flexible-tariffs', tariffData),
  updateFlexibleTariff: (id, tariffData) => api.put(`/api/flexible-tariffs/${id}`, tariffData),
  deleteFlexibleTariff: (id) => api.delete(`/api/flexible-tariffs/${id}`),
  getOperators: () => api.get('/api/operators'),
  getCities: () => api.get('/api/cities'),
  getAvailableOperators: (connectionId = null) => api.get('/api/available-operators' + (connectionId ? `?connection_id=${connectionId}` : '')),
  getAvailableCities: () => api.get('/api/available-cities'),
  getProxyTypes: () => api.get('/api/proxy-types'),
  toggleProxyStatus: (proxyId) => api.post(`/api/proxies/${proxyId}/toggle-status`),
  
  // Прокси
  getProxy: (proxyId) => api.get(`/api/proxies/${proxyId}`), // Получить один прокси с актуальным паролем клиента
  getProxies: (params = '') => api.get(`/api/proxies${params ? '?' + params : ''}`),
  getProxiesPaginated: ({ 
    limit = 100000, 
    offset = 0, 
    clientLogin = null, 
    proxyType = null, 
    isActive = null, 
    tariffPlan = null,
    availabilityStatus = null,
    serverName = null,
    selectedCity = null,
    selectedOperator = null,
    port = null,
    modem = null,
    speed = null,
    tcpConnections = null,
    modemReconnectType = null,
    modemOsfp = null,
    proxyId = null,
    autoReplaceEnabled = null,
    isKeepalive = null,
    isCompression = null,
    expiresAtFrom = null,
    expiresAtTo = null,
    expiresAtUnlimited = null,
    forceRefresh = false 
  }) => {
    const params = new URLSearchParams();
    // Всегда передаем limit, по умолчанию очень большое значение для получения всех записей
    if (limit !== null && limit !== undefined) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    
    // ИСПРАВЛЕНО: Поддержка множественных значений для всех фильтров
    if (clientLogin) {
      if (Array.isArray(clientLogin)) {
        clientLogin.forEach(val => params.append('client_login', val));
      } else {
        params.append('client_login', clientLogin);
      }
    }
    if (proxyType) {
      if (Array.isArray(proxyType)) {
        proxyType.forEach(val => params.append('proxy_type', val));
      } else {
        params.append('proxy_type', proxyType);
      }
    }
    if (isActive !== null) params.append('is_active', isActive);
    if (tariffPlan) {
      if (Array.isArray(tariffPlan)) {
        tariffPlan.forEach(val => params.append('tariff_plan', val));
      } else {
        params.append('tariff_plan', tariffPlan);
      }
    }
    if (availabilityStatus) {
      if (Array.isArray(availabilityStatus)) {
        availabilityStatus.forEach(val => params.append('availability_status', val));
      } else {
        params.append('availability_status', availabilityStatus);
      }
    }
    if (serverName) {
      // ИСПРАВЛЕНО: Поддержка множественных серверов
      if (Array.isArray(serverName)) {
        serverName.forEach(val => params.append('server_name', val));
      } else {
        params.append('server_name', serverName);
      }
    }
    if (selectedCity) {
      // ИСПРАВЛЕНО: Поддержка множественных городов
      if (Array.isArray(selectedCity)) {
        selectedCity.forEach(val => params.append('selected_city', val));
      } else {
        params.append('selected_city', selectedCity);
      }
    }
    if (selectedOperator) {
      // ИСПРАВЛЕНО: Поддержка множественных операторов
      if (Array.isArray(selectedOperator)) {
        selectedOperator.forEach(val => params.append('selected_operator', val));
      } else {
        params.append('selected_operator', selectedOperator);
      }
    }
    // ИСПРАВЛЕНО: Поддержка фильтров по порту и модему
    if (port) {
      if (Array.isArray(port)) {
        port.forEach(val => params.append('port', val));
      } else {
        params.append('port', port);
      }
    }
    if (modem) {
      if (Array.isArray(modem)) {
        modem.forEach(val => params.append('modem', val));
      } else {
        params.append('modem', modem);
      }
    }
    // ИСПРАВЛЕНО: Поддержка фильтров по скорости и TCP соединениям
    if (speed) {
      if (Array.isArray(speed)) {
        speed.forEach(val => params.append('speed', val));
      } else {
        params.append('speed', speed);
      }
    }
    if (tcpConnections) {
      if (Array.isArray(tcpConnections)) {
        tcpConnections.forEach(val => params.append('tcp_connections', val));
      } else {
        params.append('tcp_connections', tcpConnections);
      }
    }
    if (modemReconnectType) {
      if (Array.isArray(modemReconnectType)) {
        modemReconnectType.forEach(val => params.append('modem_reconnect_type', val));
      } else {
        params.append('modem_reconnect_type', modemReconnectType);
      }
    }
    if (modemOsfp) {
      if (Array.isArray(modemOsfp)) {
        modemOsfp.forEach(val => params.append('modem_osfp', val));
      } else {
        params.append('modem_osfp', modemOsfp);
      }
    }
    if (proxyId !== null && proxyId !== undefined && String(proxyId).trim() !== '') {
      params.append('proxy_id', String(proxyId));
    }
    if (autoReplaceEnabled !== null && autoReplaceEnabled !== undefined) params.append('auto_replace_enabled', autoReplaceEnabled);
    if (isKeepalive !== null && isKeepalive !== undefined) params.append('is_keepalive', isKeepalive);
    if (isCompression !== null && isCompression !== undefined) params.append('is_compression', isCompression);
    if (expiresAtFrom) params.append('expires_at_from', expiresAtFrom);
    if (expiresAtTo) params.append('expires_at_to', expiresAtTo);
    if (expiresAtUnlimited !== null && expiresAtUnlimited !== undefined) {
      params.append('expires_at_unlimited', expiresAtUnlimited);
    }
    if (forceRefresh) params.append('force_refresh', 'true');
    return api.get(`/api/proxies?${params.toString()}`);
  },
  getProxyFilterValues: (params = {}) => {
    const urlParams = new URLSearchParams();
    // Обработка параметров фильтрации (camelCase -> snake_case)
    const {
      clientLogin,
      tariffPlan,
      isActive,
      availabilityStatus,
      serverName,
      selectedCity,
      selectedOperator,
      modemReconnectType,
      modemOsfp,
      port,
      modem,
      proxyType,
      autoReplaceEnabled,
      isKeepalive,
      isCompression,
    } = params;
    
    if (clientLogin) {
      const values = Array.isArray(clientLogin) ? clientLogin : [clientLogin];
      values.forEach(val => urlParams.append('client_login', val));
    }
    if (tariffPlan) {
      const values = Array.isArray(tariffPlan) ? tariffPlan : [tariffPlan];
      values.forEach(val => urlParams.append('tariff_plan', val));
    }
    if (isActive !== null && isActive !== undefined) {
      const values = Array.isArray(isActive) ? isActive : [isActive];
      values.forEach(val => urlParams.append('is_active', val));
    }
    if (availabilityStatus) {
      const values = Array.isArray(availabilityStatus) ? availabilityStatus : [availabilityStatus];
      values.forEach(val => urlParams.append('availability_status', val));
    }
    if (serverName) {
      const values = Array.isArray(serverName) ? serverName : [serverName];
      values.forEach(val => urlParams.append('server_name', val));
    }
    if (selectedCity) {
      const values = Array.isArray(selectedCity) ? selectedCity : [selectedCity];
      values.forEach(val => urlParams.append('selected_city', val));
    }
    if (selectedOperator) {
      const values = Array.isArray(selectedOperator) ? selectedOperator : [selectedOperator];
      values.forEach(val => urlParams.append('selected_operator', val));
    }
    if (port) {
      const values = Array.isArray(port) ? port : [port];
      values.forEach(val => urlParams.append('port', val));
    }
    if (modem) {
      const values = Array.isArray(modem) ? modem : [modem];
      values.forEach(val => urlParams.append('modem', val));
    }
    if (modemReconnectType) {
      const values = Array.isArray(modemReconnectType) ? modemReconnectType : [modemReconnectType];
      values.forEach(val => urlParams.append('modem_reconnect_type', val));
    }
    if (modemOsfp) {
      const values = Array.isArray(modemOsfp) ? modemOsfp : [modemOsfp];
      values.forEach(val => urlParams.append('modem_osfp', val));
    }
    if (proxyType) {
      const values = Array.isArray(proxyType) ? proxyType : [proxyType];
      values.forEach(val => urlParams.append('proxy_type', val));
    }
    if (autoReplaceEnabled !== null && autoReplaceEnabled !== undefined) {
      const values = Array.isArray(autoReplaceEnabled) ? autoReplaceEnabled : [autoReplaceEnabled];
      values.forEach(val => urlParams.append('auto_replace_enabled', val));
    }
    if (isKeepalive !== null && isKeepalive !== undefined) {
      const values = Array.isArray(isKeepalive) ? isKeepalive : [isKeepalive];
      values.forEach(val => urlParams.append('is_keepalive', val));
    }
    if (isCompression !== null && isCompression !== undefined) {
      const values = Array.isArray(isCompression) ? isCompression : [isCompression];
      values.forEach(val => urlParams.append('is_compression', val));
    }
    
    return api.get(`/api/proxies/filter-values?${urlParams.toString()}`);
  },
  getSandboxStats: (params = {}) => {
    const urlParams = new URLSearchParams();
    const paramMap = {
      clientLogin: 'client_login',
      serverName: 'server_name',
      selectedCity: 'selected_city',
      selectedOperator: 'selected_operator',
      port: 'port',
      modem: 'modem',
      modemOsfp: 'modem_osfp',
      modemReconnectType: 'modem_reconnect_type',
      tariffPlan: 'tariff_plan',
      availabilityStatus: 'availability_status',
      proxyType: 'proxy_type',
    };
    for (const [camel, snake] of Object.entries(paramMap)) {
      const val = params[camel];
      if (val !== undefined && val !== null) {
        const values = Array.isArray(val) ? val : [val];
        values.forEach(v => urlParams.append(snake, v));
      }
    }
    if (params.isActive !== undefined && params.isActive !== null) {
      urlParams.append('is_active', params.isActive);
    }
    if (params.autoReplaceEnabled !== undefined && params.autoReplaceEnabled !== null) {
      urlParams.append('auto_replace_enabled', params.autoReplaceEnabled);
    }
    if (params.isKeepalive !== undefined && params.isKeepalive !== null) {
      urlParams.append('is_keepalive', params.isKeepalive);
    }
    if (params.isCompression !== undefined && params.isCompression !== null) {
      urlParams.append('is_compression', params.isCompression);
    }
    return api.get(`/api/proxies/sandbox-stats?${urlParams.toString()}`);
  },
  // ! УНИФИЦИРОВАННЫЙ метод создания прокси - всегда используйте этот метод
  // Работает как с flexible_tariff_id, так и с tariff_plan_name
  createProxy: (proxyData) => {
    // Создаем отдельный экземпляр axios с увеличенным таймаутом
    // * ИСПРАВЛЕНИЕ: Увеличены таймауты для предотвращения преждевременных ошибок
    const proxyQuantity = proxyData.proxy_quantity || proxyData.quantity || 1;
    // Увеличиваем таймауты: 1-5 прокси = 90с, 6-10 = 240с, >10 = 420с (7 минут)
    const timeout = proxyQuantity > 10 ? 420000 : (proxyQuantity > 5 ? 240000 : 90000);
    
    const createApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Добавляем токен авторизации
    const token = checkAndFixToken();
    if (token) {
      createApi.defaults.headers.Authorization = `Bearer ${token}`;
    }

    return createApi.post('/api/proxies', proxyData);
  },
  
  // ! УСТАРЕЛО: Используйте createProxy() с flexible_tariff_id
  // Оставлено для обратной совместимости (просто вызывает createProxy)
  createFlexibleProxy: (proxyData) => {
    return apiService.createProxy(proxyData);
  },
  
  // ! УСТАРЕЛО: Используйте createProxy() напрямую
  // Оставлено для обратной совместимости
  createProxyFromFlexibleTariff: (clientLogin, tariffId, quantity, expiresDays, city, operator, proxyType) => {
    return apiService.createProxy({
      client_login: clientLogin,
      flexible_tariff_id: tariffId,
      proxy_quantity: quantity,
      expires_days: expiresDays,
      city: city,
      mobile_operator: operator,
      proxy_type: proxyType
    });
  },
  getClientProxies: (clientLogin) => api.get(`/api/proxies/client/${clientLogin}`),
  updateProxy: (proxyId, proxyData) => api.put(`/api/proxies/${proxyId}`, proxyData),
  // Миграция прокси включает создание новой + удаление старой и может занимать >30с.
  // Используем longOperationApi, чтобы не обрывать успешную серверную операцию клиентским timeout.
  migrateProxy: (proxyId, proxyData) => longOperationApi.post(`/api/proxies/${proxyId}/migrate`, proxyData),
  bulkUpdateProxies: (proxyIds, updateData) => {
    // Создаем отдельный экземпляр axios с увеличенным таймаутом для массового обновления
    const bulkApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 300000, // 5 минут для массового обновления
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Добавляем токен авторизации
    const token = localStorage.getItem('token');
    if (token) {
      bulkApi.defaults.headers.Authorization = `Bearer ${token}`;
    }
    
    return bulkApi.put('/api/proxies/bulk-update', {
      proxy_ids: proxyIds,
      update_data: updateData
    });
  },
  bulkUpdateProxiesBackground: (proxyIds, updateData) => {
    // Запуск массового обновления в фоновом режиме
    return api.post('/api/proxies/bulk-update/background', {
      proxy_ids: proxyIds,
      update_data: updateData
    });
  },
  getBulkUpdateProxiesStatus: (taskId) => {
    // Получение статуса массового обновления
    return api.get(`/api/proxies/bulk-update/status/${taskId}`);
  },
  deleteProxy: (proxyId) => api.delete(`/api/proxies/${proxyId}`),
  // * ОПТИМИЗАЦИЯ: Массовое удаление прокси одним запросом
  bulkDeleteProxies: (proxyIds) => {
    // Создаем отдельный экземпляр axios с увеличенным таймаутом для массового удаления
    const deleteApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 300000, // 5 минут для массового удаления
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    const token = localStorage.getItem('token');
    if (token) {
      deleteApi.defaults.headers.Authorization = `Bearer ${token}`;
    }
    
    return deleteApi.post('/api/proxies/bulk-delete', { proxy_ids: proxyIds });
  },
  // * ОПТИМИЗАЦИЯ: Удаление всех прокси клиента одним запросом
  deleteClientProxies: (clientLogin) => {
    const deleteApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 300000, // 5 минут
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    const token = localStorage.getItem('token');
    if (token) {
      deleteApi.defaults.headers.Authorization = `Bearer ${token}`;
    }
    
    return deleteApi.delete(`/api/proxies/client/${clientLogin}`);
  },
  reconnectProxy: (proxyId) => api.post(`/api/proxies/${proxyId}/reconnect`),
  getReconnectStatus: (proxyId) => api.get(`/api/proxies/${proxyId}/reconnect/status`),
  replaceProxy: (proxyId, data) => api.post(`/api/proxies/${proxyId}/replace`, data),

  // Экспорт и шаблоны
  listExportTemplates: () => api.get('/api/export-templates'),
  createExportTemplate: (data) => api.post('/api/export-templates', data),
  deleteExportTemplate: (templateId) => api.delete(`/api/export-templates/${templateId}`),
  exportProxies: (payload) => api.post('/api/proxies/export', payload),
  
  // Статистика
  getStats: (params = {}) => api.get('/api/stats', { params }),
  getStatsSummary: () => api.get('/api/stats/summary'),
  getDashboardSales: (params = {}) => api.get('/api/dashboard/sales', { params }),
  /** Статистика SIM (та же БД /api/sim-cards, что и Sim Manager) */
  getSimCardsStats: () => api.get('/api/sim-cards/stats'),
  /** Системный склад по операторам (Mbps-слоты) для «Сводка по операторам» */
  getDashboardOperatorWarehouseStock: () => api.get('/api/dashboard/operator-warehouse-stock'),
  refreshDashboardCache: (params = {}) =>
    longOperationApi.post('/api/dashboard/refresh', null, { params, timeout: 180000 }),
  getDashboardModemSettingsMismatches: () => api.get('/api/dashboard/modem-settings-mismatches'),
  checkDashboardModemSettingsMismatches: () =>
    longOperationApi.post('/api/dashboard/modem-settings-mismatches/check'),
  applyDashboardModemSettingsMismatch: (proxyId) => api.post(`/api/dashboard/modem-settings-mismatches/${proxyId}/apply`),
  applyDashboardModemDefaultMismatch: (modemId) => api.post(`/api/dashboard/modem-settings-mismatches/modem/${modemId}/apply-default`),
  applyDashboardModemDefaultsBulk: (modemIds = []) =>
    longOperationApi.post('/api/dashboard/modem-settings-mismatches/modems/apply-default-bulk', {
      modem_ids: modemIds,
    }),
  
  // Статистика трафика
  getProxyTraffic: (proxyId) => api.get(`/api/proxies/${proxyId}/traffic`),
  getProxiesTrafficBatch: (proxyIds) => api.get(`/api/proxies/traffic/batch?proxy_ids=${proxyIds.join(',')}`),
  
  // Kraken - Множественные подключения (новое API)
  getKrakenConnections: () => api.get('/api/kraken-connections'),
  getKrakenConnection: (connectionId) => api.get(`/api/kraken-connections/${connectionId}`),
  getActiveKrakenConnections: (params = {}, config = {}) =>
    api.get('/api/kraken-connections/active', { params, ...config }),
  createKrakenConnection: (connection) => api.post('/api/kraken-connections', connection),
  updateKrakenConnection: (connectionId, connection) => api.put(`/api/kraken-connections/${connectionId}`, connection),
  updateKrakenConnectionPortRanges: (connectionId, data) => api.put(`/api/kraken-connections/${connectionId}/port-ranges`, data),
  connectKrakenConnection: (connectionId) => api.put(`/api/kraken-connections/${connectionId}/connect`),
  disconnectKrakenConnection: (connectionId) => api.put(`/api/kraken-connections/${connectionId}/disconnect`),
  deleteKrakenConnection: (connectionId) => deleteApi.delete(`/api/kraken-connections/${connectionId}`),
  testKrakenConnection: (connectionId) => api.post(`/api/kraken-connections/${connectionId}/test`),
  syncKrakenConnection: (connectionId) => api.post(`/api/kraken-connections/${connectionId}/sync`),
  
  // SSH и перезагрузка (longOperationApi: restart ~2 мин, reboot может не ответить)
  restartKraken: (connectionId) => longOperationApi.post(`/api/kraken-connections/${connectionId}/restart-kraken`),
  rebootKrakenServer: (connectionId) => longOperationApi.post(`/api/kraken-connections/${connectionId}/reboot-server`),
  generateAndInstallSshKey: (sshData) => api.post('/api/kraken-connections/generate-ssh-key', sshData),
  
  // Kraken - Совместимость (старое API)
  setKrakenConfig: (config) => api.post('/api/kraken/config', config),
  // sync_all_connections на бэкенде до ~600 с (parallel_sync overall_timeout); дефолтный api — 30 с → ложный таймаут
  syncKrakenModems: () =>
    longOperationApi.post('/api/kraken/ports/sync', {}, { timeout: 600000 }),
  syncAllKrakenConnectionsFull: () => api.post('/api/kraken-connections/sync-all-full'),
  syncKrakenPorts: () =>
    longOperationApi.post('/api/kraken/ports/sync', {}, { timeout: 600000 }),
  syncKrakenUsers: () => api.post('/api/kraken/sync/users'),
  syncKrakenProxies: () => api.post('/api/kraken/sync/proxies'),
  listKrakenRecoveryRuns: (limit = 20) => api.get('/api/kraken/recovery', { params: { limit } }),
  // preflight: по каждому подключению get_proxies (пагинация) + get_modems в Kraken; дефолтный api 30 с → ложный таймаут
  getKrakenRecoveryPreflight: (payload) =>
    longOperationApi.post('/api/kraken/recovery/preflight', payload, { timeout: 600000 }),
  startKrakenRecovery: (payload) => api.post('/api/kraken/recovery/start', payload),
  getKrakenRecoveryStatus: (runId) => api.get(`/api/kraken/recovery/${runId}`),
  retryKrakenRecoveryFailed: (runId) => api.post(`/api/kraken/recovery/${runId}/retry-failed`),
  // Установка Midori на сервер («Подключения → Установка»): задача в фоне + опрос статуса.
  // preflight — вход по SSH и проба сервера, до ~1 мин; остальное — быстрые вызовы.
  listMidoriPackages: () => api.get('/api/kraken/midori-install/packages'),
  // Подключения и сохранённый у них SSH — накат на сервер «из списка», без ввода SSH.
  listMidoriInstallConnections: () => api.get('/api/kraken/midori-install/connections'),
  midoriInstallPreflight: (payload) =>
    longOperationApi.post('/api/kraken/midori-install/preflight', payload, { timeout: 120000 }),
  startMidoriInstall: (payload) => api.post('/api/kraken/midori-install/start', payload),
  listMidoriInstallJobs: (limit = 20) => api.get('/api/kraken/midori-install/jobs', { params: { limit } }),
  getMidoriInstallJob: (jobId) => api.get(`/api/kraken/midori-install/jobs/${jobId}`),
  cancelMidoriInstallJob: (jobId) => api.post(`/api/kraken/midori-install/jobs/${jobId}/cancel`),
  // Полная синхронизация из Kraken: фон (202) + polling — не блокируем HTTP (nginx 60s → 504).
  // Тело: {} — бэкенд принимает объект { "proxy_ids": [...] }; раньше сырой Body(List) ломался на {} (422).
  syncProxiesWithKraken: async (options = {}) => {
    const startResponse = await api.post('/api/proxies/sync-with-kraken', {});
    const taskId = startResponse.data?.task_id;
    if (!taskId) {
      const err = new Error('Не получен task_id фоновой синхронизации');
      err.response = { data: { detail: err.message } };
      throw err;
    }
    return waitForSyncProxiesWithKrakenTask(taskId, options);
  },
  getSyncProxiesWithKrakenStatus: (taskId) =>
    api.get(`/api/proxies/sync-with-kraken/status/${encodeURIComponent(taskId)}`),
  getPendingKrakenPushCount: () => api.get('/api/kraken/sync/pending-push-count'),
  syncAllKrakenData: () => api.post('/api/kraken/sync/all'),
  forceSyncKrakenData: () => api.post('/api/kraken/sync/force'),
  // Отправка в Kraken: на бэкенде до 5 минут; дефолтный api (30 с) даёт ложную «ошибку» на UI.
  syncToKraken: () =>
    longOperationApi.post('/api/kraken/sync/to-kraken', {}, { timeout: 320000 }),
  getKrakenServers: (config = {}) => api.get('/api/kraken/servers', config),
  syncKrakenServer: (serverId) => api.post(`/api/kraken-servers/${serverId}/sync`),
  deleteKrakenServer: (serverId) => api.delete(`/api/kraken-servers/${serverId}`),
  getKrakenServerPortRanges: (serverId) => api.get(`/api/kraken-servers/${serverId}/port-ranges`),
  updateKrakenServerPortRanges: (serverId, data) => api.put(`/api/kraken-servers/${serverId}/port-ranges`, data),
  validatePortRanges: (portRanges) => api.post('/api/validate-port-ranges', { port_ranges: portRanges }),
  
  // Kraken Ports (Сетевые порты) - используем правильные endpoints нашего API
  getKrakenModems: () => api.get('/api/kraken/ports'),
  getAllKrakenModems: (options = {}) => {
    const refresh = Boolean(options && options.refresh);
    if (getAllKrakenModemsInFlight && !refresh) {
      return getAllKrakenModemsInFlight;
    }
    const request = api
      .get('/api/kraken/ports/all', {
        params: { page: 1, perpage: 10000, ...(refresh ? { refresh: 1 } : {}) },
      })
      .finally(() => {
        if (!refresh) {
          getAllKrakenModemsInFlight = null;
        }
      });
    if (!refresh) {
      getAllKrakenModemsInFlight = request;
    }
    return request;
  },
  getKrakenUsers: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/users`),
  getKrakenOsfpList: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/osfp-list`),
  createKrakenPort: (portData) => api.post('/api/kraken/ports', portData),
  updateKrakenPort: (portId, portData) => api.put(`/api/kraken/ports/${portId}`, portData),
  /** Актуальный reconnect_event и др. из Kraken по kraken_servers.id */
  getKrakenPortLiveModem: (serverId) =>
    api.get('/api/kraken/ports/live-modem', { params: { server_id: serverId } }),
  bulkUpdateKrakenPorts: (portIds, portData) => {
    // Увеличиваем таймаут для массового обновления до 10 минут (600 секунд)
    const bulkApi = axios.create({
      baseURL: api.defaults.baseURL,
      headers: api.defaults.headers,
      timeout: 600000  // 10 минут для массового обновления
    });
    
    // Добавляем интерцептор для токена авторизации (копируем логику из основного api)
    bulkApi.interceptors.request.use(
      (config) => {
        // Проверяем и исправляем токен перед каждым запросом
        const token = checkAndFixToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Отправляем данные в формате, ожидаемом бэкендом (BulkUpdatePortsRequest)
    return bulkApi.post('/api/kraken/ports/bulk-update', { 
        port_ids: portIds, 
        port_data: portData
    });
  },
  getBulkUpdateProgress: (taskId) => api.get(`/api/kraken/ports/bulk-update/progress/${taskId}`),
  deleteKrakenPort: (portId) => api.delete(`/api/kraken/ports/${portId}`),
  bulkDeleteKrakenPorts: (portIds) => api.post('/api/kraken/ports/bulk-delete', { port_ids: portIds }),
  getKrakenPortDetails: (portId) => api.get(`/api/kraken/ports/${portId}`),
  
  // Статус модемов на основе анализа прокси
  getModemsStatusByProxy: (modemId) => api.get('/api/modems/status-by-proxy', { params: { modem_id: modemId } }),
  
  // Новые функции Kraken API
  getKrakenModemsByConnection: (connectionId, filterData) => api.get(`/api/kraken-connections/${connectionId}/modems`, { params: filterData }),
  reconnectKrakenModems: (connectionId, serverIds) => api.post(`/api/kraken-connections/${connectionId}/modems/reconnect`, { id: serverIds }),
  setKrakenModemsActivity: (connectionId, serverIds, active) => api.post(`/api/kraken-connections/${connectionId}/modems/activity`, { server_ids: serverIds, active }),
  getKrakenModemTypes: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/modem-types`),
  getKrakenNetworkInterfaces: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/network-interfaces`),
  getKrakenNetworkTypes: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/network-types`),
  getKrakenOperators: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/operators`),
  setKrakenModemsOsfp: (connectionId, serverIds, osfpId, isOsfp) => api.post(`/api/kraken-connections/${connectionId}/modems/osfp`, { server_ids: serverIds, osfp_id: osfpId, is_osfp: isOsfp }),
  getKrakenOperatorStatistics: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/operator-statistics`),
  getKrakenOperatorIps: (connectionId, filterData, pagination) => api.post(`/api/kraken-connections/${connectionId}/operator-ips`, { filter: filterData, pagination }),
  getKrakenModemIps: (connectionId, filterData, pagination) => api.post(`/api/kraken-connections/${connectionId}/modem-ips`, { filter: filterData, pagination }),
  getKrakenServicesStatus: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/services-status`),
  stopKrakenService: (connectionId, serviceName) => api.post(`/api/kraken-connections/${connectionId}/services/stop`, { service_name: serviceName }),
  startKrakenService: (connectionId, serviceName) => api.post(`/api/kraken-connections/${connectionId}/services/start`, { service_name: serviceName }),
  getKrakenServerStatistics: (connectionId) => api.get(`/api/kraken-connections/${connectionId}/server-statistics`),
  getKrakenHostResources: () => api.get('/api/dashboard/kraken-host-resources'),
  
  // Статистика нагрузки портов
  getPortLoadStats: () => api.get('/api/network-ports/load-stats'),
  
  // Система
  healthCheck: () => api.get('/health'),
  // Кэш
  clearCache: () => api.post('/api/cache/clear'),
  
  // Гибридная система
  getHybridStatus: () => api.get('/api/hybrid/status'),
  getHybridConnections: () => api.get('/api/hybrid/connections'),
  getHybridProxies: () => api.get('/api/hybrid/proxies'),
  getHybridLogs: () => api.get('/api/hybrid/logs'),
  getHybridConfig: () => api.get('/api/hybrid/config'),
  updateHybridConfig: (config) => api.put('/api/hybrid/config', config),
  forceHybridSync: (data) => api.post('/api/hybrid/sync', data),
  
  // Характеристики прокси
  getProxyCharacteristics: (proxyId) => api.get(`/api/proxies/${proxyId}/characteristics`),
  updateProxyCharacteristics: (proxyId, characteristics) => api.put(`/api/proxies/${proxyId}/characteristics`, characteristics),
  
  // Система проверки прокси
  getProxyCheckerStatus: () => api.get('/api/proxy-checker/status'),
  // Проверка локаций
  getLocationCheckTariff: () => api.get('/api/location-check/tariff'),
  getLocationCheckSettings: () => api.get('/api/location-check/settings'),
  updateLocationCheckSettings: (data) => api.put('/api/location-check/settings', data),
  getLocationCheckTargets: () => api.get('/api/location-check/targets'),
  addLocationCheckTarget: (proxyId) => api.post('/api/location-check/targets', { proxy_id: proxyId }),
  deleteLocationCheckTarget: (targetId) => api.delete(`/api/location-check/targets/${targetId}`),
  pauseLocationCheckTarget: (targetId) => api.post(`/api/location-check/targets/${targetId}/pause`),
  resumeLocationCheckTarget: (targetId) => api.post(`/api/location-check/targets/${targetId}/resume`),
  runLocationCheckTargetNow: (targetId) => api.post(`/api/location-check/targets/${targetId}/run-now`),
  getLocationCheckSeries: (targetId, params = {}) =>
    api.get(`/api/location-check/targets/${targetId}/series`, { params }),
  getLocationCheckRuns: (targetId, limit = 20) =>
    api.get(`/api/location-check/targets/${targetId}/runs`, { params: { limit } }),
  tickLocationCheckWorker: (limit = 3) => api.post(`/api/location-check/worker/tick?limit=${limit}`),
  getProxyCheckerProxies: () => api.get('/api/proxy-checker/proxies'),
  getProxyChecksByIds: (proxyIds) => api.post('/api/proxy-checker/proxies/checks', { proxy_ids: proxyIds }),
  checkProxy: (proxyId, checkType = 'all', speedTestMode = 'full') => 
    api.post(`/api/proxy-checker/check/${proxyId}?check_type=${checkType}&speed_test_mode=${speedTestMode}`),
  massCheckProxies: (proxyIds, checkType = 'quick_availability') => 
    longOperationApi.post('/api/proxy-checker/mass-check', { proxy_ids: proxyIds, check_type: checkType }),
  /** Разовая проверка доступности стороннего прокси (админка, чекер). */
  proxyCheckerAdhocQuickAvailability: (payload) =>
    api.post('/api/proxy-checker/adhoc/quick-availability', payload),
  /** Очередь ручной проверки админки (PostgreSQL). */
  getAdminCheckerQueue: () => api.get('/api/proxy-checker/admin-queue'),
  putAdminCheckerQueue: (rows) => api.put('/api/proxy-checker/admin-queue', { rows }),
  getCheckRules: () => api.get('/api/proxy-checker/rules'),
  getProxyCheckerKrakenServers: () => api.get('/api/proxy-checker/kraken-servers'), // Устаревший: используйте getProxyCheckerKrakenConnections
  getProxyCheckerKrakenConnections: () => api.get('/api/proxy-checker/kraken-connections'), // Новый: возвращает подключения (Сервер 10, Сервер 7)
  syncProxyChecker: () => api.post('/api/proxy-checker/sync'),
  createCheckRule: (params) => {
    // Если это URLSearchParams, преобразуем в объект
    if (params instanceof URLSearchParams) {
      const data = {};
      for (const [key, value] of params.entries()) {
        data[key] = value;
      }
      return api.post('/api/proxy-checker/rules', data);
    }
    return api.post('/api/proxy-checker/rules', params);
  },
  deleteCheckRule: (ruleId, force = false) => api.delete(`/api/proxy-checker/rules/${ruleId}?force=${force}`),
  updateCheckRule: (ruleId, data) => api.put(`/api/proxy-checker/rules/${ruleId}`, data),
  getProblematicProxies: () => api.get('/api/proxy-checker/problematic'),
  getProxyReplacements: () => api.get('/api/proxy-checker/replacements'),
  getDetailedReplacementLogs: (params = {}) => api.get('/api/proxy-checker/detailed-replacement-logs', { params }),
  getReplacementSummary: (params = {}) => api.get('/api/proxy-checker/replacement-summary', { params }),
  // Массовая перепроверка недоступных прокси: 150+ шт. занимает 40–90 с — не обрывать клиентским timeout 30 с.
  quickCheckAllProxies: () => longOperationApi.post('/api/proxy-checker/quick-check-all'),
  quickCheckModemProxies: (serverId) => api.post(`/api/proxy-checker/quick-check-modem/${serverId}`),
  rebootModem: (modemId) => longOperationApi.post(`/api/modems/${modemId}/reboot`),
  rebootModemsBulk: (modemIds = []) => longOperationApi.post('/api/modems/reboot-bulk', { modem_ids: modemIds }),
  refreshModemIdentity: (modemId) => longOperationApi.post(`/api/modems/${modemId}/refresh-identity`),
  refreshModemsIdentityBulk: (modemIds = []) => longOperationApi.post('/api/modems/refresh-identity-bulk', { modem_ids: modemIds }),
  getModemRadioMetrics: (modemId) => longOperationApi.get(`/api/modems/${modemId}/radio-metrics`),
  setModemBandPolicy: (modemId, bands = []) =>
    longOperationApi.post(`/api/modems/${modemId}/band-policy`, { bands }),
  setModemsBandPolicyBulk: (modemIds = [], bands = []) =>
    longOperationApi.post('/api/modems/band-policy-bulk', { modem_ids: modemIds, bands }),
  rebootDashboardUnavailableModems: () => longOperationApi.post('/api/dashboard/modems/reboot-unavailable'),
  getModemRebootStatus: ({ modem_ids: modemIds = [], task_id: taskId } = {}) => {
    const searchParams = new URLSearchParams();
    if (Array.isArray(modemIds)) {
      modemIds.forEach((modemId) => {
        if (modemId !== undefined && modemId !== null && modemId !== '') {
          searchParams.append('modem_ids', String(modemId));
        }
      });
    }
    if (taskId) {
      searchParams.append('task_id', String(taskId));
    }
    const suffix = searchParams.toString();
    return api.get(suffix ? `/api/modems/reboot-status?${suffix}` : '/api/modems/reboot-status');
  },
  getModemRebootLogs: (params = {}) => api.get('/api/modems/reboot-logs', { params }),
  
  // Логи создания прокси
  getProxyCreationLogs: (params = {}) => api.get('/api/proxy-creation-logs', { params }),
  clearProxyCreationLogs: () => api.delete('/api/proxy-creation-logs'),

  // Системные логи
  getSystemLogs: (params = {}) => api.get('/api/system-logs', { params }),
  clearSystemLogs: (days = 30) => api.delete('/api/system-logs', { params: { days } }),
  
  // Управление пользователями (только для администраторов)
  createUser: (userData) => api.post('/api/auth/register', userData),
  updateUser: (userId, userData) => api.put(`/api/auth/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/api/auth/users/${userId}`),
  
  // Резервное копирование прокси
  exportProxiesBackup: async (connectionIds = null, allServers = false) => {
    const date = new Date().toISOString().split('T')[0];
    await downloadAuthenticatedPostFile(
      '/api/backup/export',
      {
        connection_ids: connectionIds,
        all_servers: allServers,
      },
      `proxies_backup_${date}.xlsx`,
    );
  },
  importProxiesBackup: (file, syncToKraken = true, onUploadProgress) => {
    const uploadFile = file?.originFileObj || file;
    const formData = new FormData();
    formData.append('file', uploadFile);
    return api.post(`/api/backup/import?sync_to_kraken=${syncToKraken}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: PROXY_BACKUP_IMPORT_TIMEOUT_MS,
      onUploadProgress,
    });
  },

  /** Экспорт / импорт SIM-менеджера (Excel, тот же формат что на странице Sim Manager) */
  exportSimCardsBackup: async () => {
    const date = new Date().toISOString().split('T')[0];
    await downloadAuthenticatedFile('/api/sim-cards/export/xlsx', `sim_cards_backup_${date}.xlsx`);
  },
  importSimCardsBackup: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/sim-cards/import/xlsx', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000,
    });
  },

  /** Экспорт / импорт таблицы «Сетевые порты» (Excel) */
  exportNetworkPortsXlsx: async (serverIds = null) => {
    const date = new Date().toISOString().split('T')[0];
    const params = new URLSearchParams();
    if (Array.isArray(serverIds) && serverIds.length > 0) {
      params.set(
        'server_ids',
        serverIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
          .join(','),
      );
    }
    const query = params.toString();
    const urlPath = query
      ? `/api/kraken/ports/export/xlsx?${query}`
      : '/api/kraken/ports/export/xlsx';
    await downloadAuthenticatedFile(urlPath, `network_ports_${date}.xlsx`);
  },
  importNetworkPortsXlsx: (file) => {
    const uploadFile = file?.originFileObj || file;
    const formData = new FormData();
    formData.append('file', uploadFile);
    return api.post('/api/kraken/ports/import/xlsx', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000,
    });
  },
  listAutoBackups: () => api.get('/api/backup/auto/list'),
  downloadAutoBackup: async (filename) => {
    await downloadAuthenticatedFile(`/api/backup/auto/download/${filename}`, filename);
  },

  // Полный бэкап системы (на сервере долго: pg_dump, архив, pg_restore — до 1 ч)
  createFullBackup: () =>
    api.post('/api/backup/full', null, {
      timeout: FULL_BACKUP_TIMEOUT_MS,
    }),
  validateFullBackupImport: (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/backup/full/import?dry_run=true', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: FULL_BACKUP_TIMEOUT_MS,
      onUploadProgress,
    });
  },
  importFullBackup: (file, restoreSystemConfigs = false, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({
      confirm_destructive: 'true',
      restore_system_configs: restoreSystemConfigs ? 'true' : 'false',
    });
    return api.post(`/api/backup/full/import?${params.toString()}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: FULL_BACKUP_TIMEOUT_MS,
      onUploadProgress,
    });
  },
  validateFullBackupRestore: (filename, source = 'auto') => {
    const params = new URLSearchParams({
      dry_run: 'true',
      source,
    });
    return api.post(`/api/backup/full/restore/${encodeURIComponent(filename)}?${params.toString()}`, null, {
      timeout: FULL_BACKUP_TIMEOUT_MS,
    });
  },
  restoreFullBackupFromStorage: (filename, restoreSystemConfigs = false, source = 'auto') => {
    const params = new URLSearchParams({
      confirm_destructive: 'true',
      restore_system_configs: restoreSystemConfigs ? 'true' : 'false',
      source,
    });
    return api.post(`/api/backup/full/restore/${encodeURIComponent(filename)}?${params.toString()}`, null, {
      timeout: FULL_BACKUP_TIMEOUT_MS,
    });
  },
  listFullBackups: () => api.get('/api/backup/full/list'),
  deleteFullBackup: (filename) => api.delete(`/api/backup/full/${filename}`),
  downloadFullBackup: async (filename, source = 'auto') => {
    await downloadAuthenticatedFile(`/api/backup/full/download/${filename}`, filename, source);
  },
  transferBackupSSH: (data) =>
    api.post('/api/backup/full/transfer', data, { timeout: 600000 }),
  transferToConnectedServer: (filename) =>
    api.post(`/api/backup/full/transfer-to-server/${filename}`, null, { timeout: 600000 }),

  // SSH конфигурация для бэкапов
  getBackupSSHConfig: () => api.get('/api/backup/ssh-config'),
  saveBackupSSHConfig: (data) => api.post('/api/backup/ssh-config', data),
  deleteBackupSSHConfig: () => api.delete('/api/backup/ssh-config'),
  testBackupSSHConnection: () => api.post('/api/backup/ssh-config/test'),
  generateBackupSSHKey: () => api.post('/api/backup/ssh-config/generate-key'),
  installBackupSSHKey: () => api.post('/api/backup/ssh-config/install-key'),

  // Универсальные методы для совместимости
  get: (url, params = {}) => api.get(url, { params }),
  post: (url, data = {}) => api.post(url, data),
  put: (url, data = {}) => api.put(url, data),
  delete: (url) => api.delete(url),
  request: (method, url, config = {}) => api.request({ method, url, ...config })}; 

export default api;
